import { useSelector, useDispatch } from 'react-redux';

import { LoadingStatus } from 'src/constants/loading-status';
import {
  createJiraIssue,
  createPrJiraIssue,
  setFormErrorState,
} from 'src/redux/jira/actions';
import { IssueCreationFailureReason } from 'src/redux/jira/constants';
import { Project, IssueType } from 'src/redux/jira/types';
import { BucketState } from 'src/types/state';

export const useIssueCreation = (
  selectedProject: Project | undefined,
  selectedIssueType: IssueType | undefined,
  issueSummary: string,
  commentId?: number,
  pullRequestId?: number | null
) => {
  const dispatch = useDispatch();
  const { issueCreationStatus, issueCreationFailureReason } = useSelector<
    BucketState,
    {
      issueCreationStatus: LoadingStatus;
      issueCreationFailureReason: IssueCreationFailureReason;
    }
  >(state => {
    if (!commentId) {
      return {
        issueCreationStatus:
          state.jira.createJiraIssue.jiraTabCreateForm.status,
        issueCreationFailureReason:
          state.jira.createJiraIssue.jiraTabCreateForm.failureReason,
      };
    } else {
      const form = state.jira.createJiraIssue.createForm[commentId];
      if (form) {
        return {
          issueCreationStatus: form.status,
          issueCreationFailureReason: form.failureReason,
        };
      }

      return {
        issueCreationStatus: LoadingStatus.Before,
        issueCreationFailureReason: IssueCreationFailureReason.None,
      };
    }
  });

  const createJiraTabJiraIssue = () => {
    if (selectedProject && selectedIssueType && issueSummary) {
      dispatch(
        setFormErrorState({
          status: LoadingStatus.Before,
          failureReason: IssueCreationFailureReason.None,
        })
      );
      dispatch(
        createJiraIssue({
          cloudId: selectedProject.site.cloudId,
          projectId: selectedProject.id,
          issueTypeId: selectedIssueType!.id,
          summary: issueSummary,
          projectKey: selectedProject.key,
          projectName: selectedProject.name,
        })
      );
    }
  };

  const createPullRequestJiraIssue = () => {
    if (
      selectedProject &&
      pullRequestId &&
      commentId &&
      selectedIssueType &&
      issueSummary
    ) {
      dispatch(
        createPrJiraIssue({
          cloudId: selectedProject.site.cloudId,
          projectId: selectedProject.id,
          issueTypeId: selectedIssueType!.id,
          summary: issueSummary,
          commentId,
          pullRequestId,
          type: 'PrCommentIssue',
        })
      );
    }
  };

  const isCreatingIssue = issueCreationStatus === LoadingStatus.Fetching;
  const isInvalid =
    issueCreationFailureReason === IssueCreationFailureReason.Unknown ||
    issueCreationFailureReason === IssueCreationFailureReason.UnsupportedFields;

  return {
    issueCreationStatus,
    issueCreationFailureReason,
    createPullRequestJiraIssue,
    createJiraTabJiraIssue,
    isCreatingIssue,
    isInvalid,
  };
};
